import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../context/AppContext";
import EditableForm from "../EditableForm";
import { createProjectCompany, updateProjectCompany } from "../../api/projects";

const CreateCompanyDialog = ({ open, onClose, onSuccess, company = null }) => {
    const { t } = useTranslation();
    const { setIsLoading } = useAppContext();
    const [error, setError] = useState(null);

    const [form, setForm] = useState({
        name: "",
        fiscal_code: "",
        address: "",
        country: "",
        industry: "",
        founded_at: "",
        contact_person_name: "",
        contact_person_email: "",
        contact_person_phone: "",
    });

    useEffect(() => {
        if (company) {
            setForm({
                name: company.name || "",
                fiscal_code: company.fiscal_code || "",
                address: company.address || "",
                country: company.country || "",
                industry: company.industry || "",
                founded_at: company.founded_at?.split("T")[0] || "",
                contact_person_name: company.contact_person_name || "",
                contact_person_email: company.contact_person_email || "",
                contact_person_phone: company.contact_person_phone || "",
            });
        }
    }, [company]);

    const handleSubmit = async (data) => {
        setIsLoading(true);
        try {
            if (company?.id) {
                await updateProjectCompany(company.id, data);
            } else {
                await createProjectCompany(data);
            }
            setError(null);
            onSuccess?.();
            setForm({
                name: "",
                fiscal_code: "",
                address: "",
                country: "",
                industry: "",
                founded_at: "",
                contact_person_name: "",
                contact_person_email: "",
                contact_person_phone: "",
            });
            onClose();
        } catch (err) {
            console.error("Save error", err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const fields = [
        { name: "name", label: t("companies.name"), required: true },
        { name: "fiscal_code", label: t("companies.fiscal_code") },
        { name: "address", label: t("companies.address"), multiline: true },
        { name: "country", label: t("companies.country") },
        { name: "industry", label: t("companies.industry") },
        { name: "founded_at", label: t("companies.founded_at"), type: "date" },
        { name: "contact_person_name", label: t("companies.contact_person_name") },
        { name: "contact_person_email", label: t("companies.contact_person_email") },
        { name: "contact_person_phone", label: t("companies.contact_person_phone") },
    ];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ color: "#000" }}>
                {company ? t("companies.edit_company") : t("companies.create_company")}
            </DialogTitle>
            <DialogContent sx={{ paddingTop: "5px !important" }}>
                {error && (
                    <Box mb={2}>
                        <Box
                            sx={{
                                bgcolor: "#fdecea",
                                color: "#b71c1c",
                                px: 2,
                                py: 1.5,
                                borderRadius: 1,
                                fontSize: 14,
                                fontWeight: 500,
                            }}
                        >
                            {t(`${error}`) || error}
                        </Box>
                    </Box>
                )}
                <EditableForm
                    data={form}
                    fields={fields}
                    onChange={setForm}
                    onSubmit={handleSubmit}
                    hideSubmitButton={true}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ color: "#000" }}>
                    {t("actions.cancel")}
                </Button>
                <Button onClick={() => handleSubmit(form)} sx={{ color: "#000" }}>
                    {company ? t("actions.update") : t("actions.create")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateCompanyDialog;
